import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";

function SortableRule({ rule, index, onUpdate, onRemove, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id || `rule-${index}`,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="crm-workflow__canvas-node">
      <button type="button" className="crm-workflow__drag" {...attributes} {...listeners}>
        <GripVertical size={16} />
      </button>
      <div className="crm-workflow__canvas-body">{children}</div>
      <button type="button" onClick={() => onRemove(index)} aria-label="Remove rule">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

const CrmWorkflowCanvas = ({ rules, onChange, renderRuleEditor }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = rules.map((r, i) => r.id || `rule-${i}`);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(rules, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="crm-workflow__canvas">
          {rules.map((rule, index) => (
            <SortableRule
              key={rule.id || index}
              rule={rule}
              index={index}
              onUpdate={(patch) =>
                onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)))
              }
              onRemove={() => onChange(rules.filter((_, i) => i !== index))}
            >
              {renderRuleEditor(rule, index)}
            </SortableRule>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default CrmWorkflowCanvas;
